import React, { useRef, useEffect } from 'react';

/**
 * Background — fixed full-viewport image (dna_blockchain.jpg) with overlay,
 * grain, theme-aware filters, mouse-drift parallax, slow scroll-zoom and
 * occasional tx-pulse halos.
 *
 * Reads the same image the rest of the site already uses:
 *   public/assets/images/dna_blockchain.jpg
 */
export default function Background() {
  const imgRef = useRef(null);
  const wrapRef = useRef(null);

  // mouse + scroll + breath, composed on a single transform via rAF
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const m = { tx: 0, ty: 0, x: 0, y: 0 };
    let sz = 0;
    let raf = null;

    const onMouse = (e) => {
      m.tx = (e.clientX / window.innerWidth - 0.5) * 10;
      m.ty = (e.clientY / window.innerHeight - 0.5) * 7;
    };
    const onTouch = (e) => {
      if (!e.touches[0]) return;
      m.tx = (e.touches[0].clientX / window.innerWidth - 0.5) * 10;
      m.ty = (e.touches[0].clientY / window.innerHeight - 0.5) * 7;
    };
    const onScroll = () => {
      sz = Math.min(window.scrollY * 0.00002, 0.06);
    };

    window.addEventListener('mousemove', onMouse, { passive: true });
    window.addEventListener('touchmove', onTouch, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    const t0 = performance.now();
    const tick = (now) => {
      const t = (now - t0) * 0.001;
      m.x += (m.tx - m.x) * 0.05;
      m.y += (m.ty - m.y) * 0.05;
      const breath = 1 + Math.sin(t * (Math.PI * 2 / 22)) * 0.006;
      const scale = breath + sz;
      img.style.transform = `translate3d(${m.x.toFixed(2)}px, ${m.y.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('touchmove', onTouch);
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // tx-pulse halos
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const fire = () => {
      const pulse = document.createElement('div');
      pulse.className = 'lv2-tx-pulse';
      pulse.style.left = (15 + Math.random() * 70) + '%';
      pulse.style.top = (15 + Math.random() * 70) + 'vh';
      wrap.appendChild(pulse);
      setTimeout(() => { if (pulse.parentNode) pulse.remove(); }, 2000);
    };

    const first = setTimeout(fire, 1500);
    const id = setInterval(fire, 5500);

    return () => {
      clearTimeout(first);
      clearInterval(id);
    };
  }, []);

  return (
    <div className="lv2-bg" ref={wrapRef} aria-hidden="true">
      <div className="lv2-bg-img" ref={imgRef} />
      <div className="lv2-bg-overlay" />
      <div className="lv2-bg-grain" />
    </div>
  );
}
